import React, { useState, useEffect, useRef } from "react";
import { 
  RefreshCw, Download, ExternalLink, ArrowLeft, Eye, Clock, Sparkles, FastForward, X
} from "lucide-react";
import { motion } from 'motion/react';
import { Video, getProxiedThumbnailUrl } from "../types";

interface VideoDetailModalProps {
  video: Video;
  allVideos: Video[];
  isAdmin?: boolean;
  onClose: () => void;
  onVideoSelect: (video: Video) => void;
  siteName?: string;
  watermarkText?: string;
  adVideoUrl?: string;
  adLinkUrl?: string;
  adEnabled?: boolean;
  adSkipSeconds?: number;
}

export default function VideoDetailModal({
  video,
  allVideos,
  isAdmin,
  onClose,
  onVideoSelect,
  siteName,
  watermarkText,
  adVideoUrl,
  adLinkUrl,
  adEnabled,
  adSkipSeconds,
}: VideoDetailModalProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [iframeKey, setIframeKey] = useState(0);
  const [hasStreamError, setHasStreamError] = useState(false);
  const [isTheaterMode, setIsTheaterMode] = useState(false);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [currentDuration, setCurrentDuration] = useState(video.duration || "");
  const [bufferState, setBufferState] = useState<{
    bufferAhead: number;
    bufferedPercent: number;
    isBuffering: boolean;
  }>({
    bufferAhead: 0,
    bufferedPercent: 0,
    isBuffering: false
  });
  
  const [heroStart, setHeroStart] = useState("");
  const [heroEnd, setHeroEnd] = useState("");
  const [isSettingHero, setIsSettingHero] = useState(false);

  // Helper to extract clean raw URL
  const getCleanUrl = (rawUrl: string) => {
    let clean = rawUrl || "";
    if (clean.includes("src=")) {
      const srcMatch = clean.match(/src=["']([^"']+)["']/i);
      if (srcMatch && srcMatch[1]) clean = srcMatch[1];
    }
    if (clean.startsWith("/api/proxy?url=")) {
      clean = decodeURIComponent(clean.replace("/api/proxy?url=", ""));
    }
    if (clean.startsWith("/api/iframe-proxy?url=")) {
      clean = decodeURIComponent(clean.replace("/api/iframe-proxy?url=", ""));
    }
    return clean.split('"')[0].split("'")[0].split(" ")[0].replace(/[><].*$/, "").trim();
  };
  
  const cleanUrl = getCleanUrl(video.videoUrl);

  // Get the proxied iframe URL
  const getProxiedIframeUrl = (url: string) => {
    if (!url) return "";
    let target = url;
    if (target.startsWith("//")) {
      target = "https:" + target;
    } else if (!target.startsWith("http://") && !target.startsWith("https://")) {
      if (target.startsWith("/")) {
        if (target.includes("get_file") || target.includes("videos_screenshots") || target.includes("embed")) {
          target = "https://kama-baba.com" + target;
        } else {
          target = "https://striptube.cc" + target;
        }
      } else {
        target = "https://" + target;
      }
    }
    return `/api/iframe-proxy?url=${encodeURIComponent(target)}&dur=${encodeURIComponent(video.duration || "")}`;
  };

  const iframeSrc = getProxiedIframeUrl(cleanUrl);

  // Pre-warm server stream cache and parallel prefetch pipeline immediately on modal open
  useEffect(() => {
    if (cleanUrl) {
      try {
        fetch(`/api/warmup?url=${encodeURIComponent(cleanUrl)}`).catch(() => {});
      } catch (e) {}
    }
  }, [cleanUrl]);

  const [isShowingAd, setIsShowingAd] = useState(false);
  const [adCountdown, setAdCountdown] = useState(adSkipSeconds || 5);
  const [canSkipAd, setCanSkipAd] = useState(false);
  const [isFullWindow, setIsFullWindow] = useState(false);

  // Dynamic Floating Intermittent Watermark Position
  const [watermarkPos, setWatermarkPos] = useState({ top: "12%", left: "12%" });
  const [watermarkVisible, setWatermarkVisible] = useState(true);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Intermittent watermark trigger
  useEffect(() => {
    const interval = setInterval(() => {
      setWatermarkVisible(prev => {
        if (prev) {
          return false;
        } else {
          const randomTop = Math.floor(Math.random() * 55) + 15;
          const randomLeft = Math.floor(Math.random() * 55) + 15;
          setWatermarkPos({ top: `${randomTop}%`, left: `${randomLeft}%` });
          return true;
        }
      });
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  const handleSkipAd = () => {
    setIsShowingAd(false);
  };

  const handlePlayNextVideo = () => {
    setHasStreamError(false);
    if (recommendations.length > 0) {
      onVideoSelect(recommendations[0]);
    } else {
      const currentIndex = allVideos.findIndex((v) => v.id === video.id);
      if (currentIndex !== -1 && allVideos[currentIndex + 1]) {
        onVideoSelect(allVideos[currentIndex + 1]);
      } else if (allVideos.length > 0) {
        onVideoSelect(allVideos[0]);
      }
    }
  };
  // Detailed Console Logging for HTML5 Video Element inside the iframe
  useEffect(() => {
    let videoEl: HTMLVideoElement | null = null;
    let cleanupEvents: (() => void) | null = null;
    let pollInterval = setInterval(() => {
      try {
        const iframe = document.getElementById('kama-baba-iframe-player') as HTMLIFrameElement;
        if (!iframe || !iframe.contentWindow || !iframe.contentWindow.document) return;

        const video = iframe.contentWindow.document.querySelector('video');
        if (!video) return;
        
        if (video === videoEl) return; // Already attached to this video element
        
        if (cleanupEvents) cleanupEvents();
        videoEl = video;
        console.log('[VideoTracker] 📺 Found HTML5 Video Element:', videoEl);

        const handleLoadStart = () => console.log(`[VideoTracker] 🔄 loadstart | Source: ${videoEl?.src || videoEl?.currentSrc}`);
        const handleCanPlay = () => console.log(`[VideoTracker] 🟢 canplay | Ready state: ${videoEl?.readyState}`);
        const handleWaiting = () => console.log(`[VideoTracker] ⏳ waiting | Buffering...`);
        const handlePlaying = () => console.log(`[VideoTracker] ▶️ playing`);
        const handleStalled = () => console.warn(`[VideoTracker] ⚠️ stalled | No data received for playback`);
        const handleError = () => {
          const err = videoEl?.error;
          console.error(`[VideoTracker] ❌ error | Code: ${err?.code}, Message: ${err?.message || 'Unknown stream error'}, Source: ${videoEl?.src || videoEl?.currentSrc}`);
        };
        const handleAbort = () => console.warn(`[VideoTracker] 🛑 abort | Source loading aborted`);
        const handleEmptied = () => console.warn(`[VideoTracker] 🕳️ emptied | Source transitions/reset`);
        const handleLoadedData = () => console.log(`[VideoTracker] 🔗 loadeddata | Current Src: ${videoEl?.currentSrc}`);

        videoEl.addEventListener('loadstart', handleLoadStart);
        videoEl.addEventListener('canplay', handleCanPlay);
        videoEl.addEventListener('waiting', handleWaiting);
        videoEl.addEventListener('playing', handlePlaying);
        videoEl.addEventListener('stalled', handleStalled);
        videoEl.addEventListener('error', handleError);
        videoEl.addEventListener('abort', handleAbort);
        videoEl.addEventListener('emptied', handleEmptied);
        videoEl.addEventListener('loadeddata', handleLoadedData);

        cleanupEvents = () => {
          if (!videoEl) return;
          videoEl.removeEventListener('loadstart', handleLoadStart);
          videoEl.removeEventListener('canplay', handleCanPlay);
          videoEl.removeEventListener('waiting', handleWaiting);
          videoEl.removeEventListener('playing', handlePlaying);
          videoEl.removeEventListener('stalled', handleStalled);
          videoEl.removeEventListener('error', handleError);
          videoEl.removeEventListener('abort', handleAbort);
          videoEl.removeEventListener('emptied', handleEmptied);
          videoEl.removeEventListener('loadeddata', handleLoadedData);
        };
      } catch (err) {
        // Silently ignore cross-origin or unloaded iframe errors
      }
    }, 1000);

    return () => {
      clearInterval(pollInterval);
      if (cleanupEvents) cleanupEvents();
    };
  }, [iframeSrc, iframeKey]);



  // Listen for messages from player iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'VS_PLAY_NEXT') {
        handlePlayNextVideo();
      } else if (event.data?.type === 'VS_STREAM_ERROR') {
        setHasStreamError(true);
      } else if (event.data?.type === 'VS_THEATER_MODE') {
        setIsTheaterMode(!!event.data.enabled);
      } else if (event.data?.type === 'VS_FULL_WINDOW') {
        setIsFullWindow(!!event.data.enabled);
      } else if (event.data?.type === 'VS_BUFFER_UPDATE') {
        setBufferState({
          bufferAhead: event.data.bufferAhead ?? 0,
          bufferedPercent: event.data.bufferedPercent ?? 0,
          isBuffering: !!event.data.isBuffering
        });
      } else if (event.data?.type === 'video-duration' && event.data.duration) {
        setCurrentDuration(event.data.duration);
        video.duration = event.data.duration;
        fetch(`/api/videos/${video.id}/duration`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ duration: event.data.duration })
        }).catch(console.error);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [recommendations, allVideos, video.id, onVideoSelect, video.duration]);

  // Auto-scroll to top on video change and record view
  useEffect(() => {
    setIsLoading(true);
    setHasStreamError(false);
    setCurrentDuration(video.duration || "");
    setBufferState({ bufferAhead: 0, bufferedPercent: 0, isBuffering: false });
    setIsShowingAd(false);
    setAdCountdown(adSkipSeconds || 5);
    setCanSkipAd(false);
    
    // Auto scroll up on video click instantly
    window.scrollTo({ top: 0, behavior: 'auto' });
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }

    // Track video view in DB on open
    fetch(`/api/videos/${video.id}/view`, { method: "POST" })
      .catch((err) => console.error("View log failed", err));
  }, [video.id, video.videoUrl]);

  // Handle Close with Escape Key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isFullWindow) {
          setIsFullWindow(false);
          const iframe = document.getElementById('kama-baba-iframe-player') as HTMLIFrameElement;
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({ type: 'VS_EXIT_FULL_WINDOW' }, '*');
          }
        } else {
          onClose();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, isFullWindow]);

  // Prevent underlying body from scrolling while modal is active
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalStyle || "unset";
    };
  }, []);

  // Get recommendations only once per video
  useEffect(() => {
    const others = allVideos.filter((v) => v.id !== video.id);
    const len = others.length;
    if (len === 0) {
      setRecommendations([]);
      return;
    }
    
    const pool = [...others];
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }

    const targetCount = Math.max(pool.length, 12);
    const finalCount = Math.min(targetCount, 200);

    const shuffled = [];
    for (let i = 0; i < finalCount; i++) {
      const originalVideo = pool[i % len];
      shuffled.push({
        ...originalVideo,
        uniqueId: `${originalVideo.id}-rec-${i}`
      });
    }
    
    setRecommendations(shuffled);
  }, [video.id]); // Deliberately omit allVideos to prevent reshuffling on background polling

  return (
    <div
      ref={scrollContainerRef}
      id="video-detail-backdrop"
      className="fixed inset-0 z-50 overflow-y-auto bg-black animate-fadeIn"
    >
      <div className={`mx-auto px-4 py-4 sm:py-6 flex flex-col pb-10 transition-all duration-300 ${isTheaterMode ? 'max-w-7xl' : 'max-w-5xl'}`}>
        
        {/* Top Navigation Bar */}
        <div className="w-full mb-4 flex items-center justify-between gap-4 flex-wrap">
          <motion.button 
            onClick={onClose}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-900 hover:bg-zinc-800 text-green-400 rounded-full text-sm font-semibold transition-colors border border-green-500/30 shadow-md shadow-green-500/10 cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Directory
          </motion.button>

          <div className="flex items-center gap-2">
            <button 
              onClick={() => {
                setIsLoading(true);
                setIframeKey(k => k + 1);
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900/80 hover:bg-zinc-800 text-zinc-300 hover:text-green-400 rounded-lg text-xs font-mono border border-green-500/20 transition-all cursor-pointer"
              title="Reload Video Player"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin text-green-400' : ''}`} />
              <span>Reload</span>
            </button>

            <button 
              onClick={handlePlayNextVideo}
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-green-500 hover:bg-green-400 text-black rounded-lg text-xs font-bold transition-all cursor-pointer uppercase tracking-wider shadow-[0_0_15px_rgba(34,197,94,0.3)]"
              title="Play Next Video"
            >
              <span>Next Video ➔</span>
            </button>
          </div>
        </div>

        {/* Stream Error Notice Banner */}
        {hasStreamError && (
          <div className="w-full bg-red-950/80 border border-red-500/40 rounded-xl p-3.5 mb-4 flex items-center justify-between gap-3 text-red-200 text-xs shadow-lg">
            <span className="font-medium flex items-center gap-2">
              <span className="text-base">⚠️</span> Video stream removed or unavailable from host.
            </span>
            <button
              onClick={handlePlayNextVideo}
              className="px-3 py-1.5 bg-green-500 hover:bg-green-400 text-black font-bold rounded-lg text-xs uppercase tracking-wide cursor-pointer transition-transform hover:scale-105 shrink-0"
            >
              Play Next Video ➔
            </button>
          </div>
        )}

        <div className="w-full flex flex-col">
          
          {/* Main Kama Baba Style Iframe Video Player Container */}
          <div className={isFullWindow 
            ? "fixed inset-0 z-[9999999] w-screen h-screen m-0 p-0 rounded-none border-none bg-black overflow-hidden" 
            : "relative h-[55vh] sm:h-[65vh] md:h-[75vh] overflow-hidden -mx-4 sm:mx-0 w-[calc(100%+2rem)] sm:w-full rounded-none sm:rounded-2xl shadow-[0_0_30px_rgba(34,197,94,0.15)] border border-green-500/30 bg-black mb-6"
          }>
            
            {/* Full-Window Exit Button */}
            {isFullWindow && (
              <button
                onClick={() => {
                  setIsFullWindow(false);
                  const iframe = document.getElementById('kama-baba-iframe-player') as HTMLIFrameElement;
                  if (iframe && iframe.contentWindow) {
                    iframe.contentWindow.postMessage({ type: 'VS_EXIT_FULL_WINDOW' }, '*');
                  }
                }}
                className="absolute top-4 right-4 z-50 bg-black/80 hover:bg-black text-white hover:text-green-400 px-3.5 py-1.5 rounded-full text-xs font-mono font-bold flex items-center gap-1.5 border border-white/20 transition cursor-pointer backdrop-blur-md shadow-2xl"
              >
                <X className="w-4 h-4" />
                <span>Exit Full Size</span>
              </button>
            )}
            
            {/* Loading Indicator */}
            {isLoading && (
              <div className="absolute inset-0 z-20 bg-zinc-950 flex flex-col justify-end p-4 sm:p-6 overflow-hidden">
                {/* Simulated pulsing background */}
                <div className="absolute inset-0 bg-zinc-900 animate-pulse opacity-30"></div>
                
                {/* Simulated player controls skeleton */}
                <div className="relative z-30 w-full mt-auto opacity-70">
                   <div className="w-full h-1.5 bg-zinc-800 rounded-full mb-4 overflow-hidden">
                     <div className="h-full bg-zinc-700 w-1/3 animate-pulse rounded-full"></div>
                   </div>
                   <div className="flex items-center justify-between">
                     <div className="flex items-center gap-4">
                       <div className="w-8 h-8 rounded-full bg-zinc-800 animate-pulse"></div>
                       <div className="w-6 h-6 rounded bg-zinc-800 animate-pulse"></div>
                       <div className="w-20 h-4 rounded bg-zinc-800 animate-pulse"></div>
                     </div>
                     <div className="flex items-center gap-4">
                       <div className="w-6 h-6 rounded bg-zinc-800 animate-pulse"></div>
                       <div className="w-6 h-6 rounded bg-zinc-800 animate-pulse"></div>
                     </div>
                   </div>
                </div>
              </div>
            )}

                        {/* Native Kama Baba Iframe Embed */}
            {/* Background Stream Preloaders */}
            
            
            
            <iframe
              key={`${iframeSrc}-${iframeKey}`}
              id="kama-baba-iframe-player"
              src={iframeSrc}
              title={video.title}
              className="w-full h-full border-0 absolute inset-0 z-10 bg-black"
              allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
              allowFullScreen
              sandbox="allow-scripts allow-same-origin allow-forms allow-presentation"
              onLoad={() => setIsLoading(false)}
            />
          </div>

          {/* Video Meta Information */}
          <div className="w-full">
            <h1 className="text-xl sm:text-2xl font-bold text-white leading-tight mb-2 font-sans tracking-tight">
              {video.title}
            </h1>
            
            <div className="flex items-center gap-4 text-zinc-400 text-sm font-mono mb-6 flex-wrap">
              <span className="flex items-center gap-1.5 text-green-400">
                <Eye className="w-4 h-4" />
                {video.views.toLocaleString()} views
              </span>
              <span className="flex items-center gap-1.5 text-zinc-400">
                <Clock className="w-4 h-4" />
                Duration: <strong className="text-white">{currentDuration || video.duration}</strong>
              </span>
              {isAdmin && (bufferState.bufferAhead > 0 || bufferState.isBuffering) && (
                <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-950/70 border border-emerald-500/40 text-emerald-400 text-xs font-mono">
                  <span className={`w-1.5 h-1.5 rounded-full ${bufferState.isBuffering ? 'bg-amber-400 animate-ping' : 'bg-emerald-400 animate-pulse'}`}></span>
                  {bufferState.isBuffering 
                    ? "Buffering Stream..." 
                    : `Prebuffered: ${bufferState.bufferAhead}s ahead`
                  }
                </span>
              )}
              {video.category && (
                <span className="px-2.5 py-0.5 rounded-full bg-green-500/10 text-green-400 text-xs font-semibold border border-green-500/20">
                  {video.category}
                 </span>
              )}
            </div>

            <div className="w-full h-px bg-zinc-900 mb-6"></div>

            {/* Admin Cosmic Banner Controls */}
            {isAdmin && (
              <div className="p-4 sm:p-5 rounded-xl bg-green-950/20 border border-green-500/40 mb-8 space-y-4">
                <h3 className="text-green-400 font-bold text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Cosmic Streaming Interface Control
                </h3>
                <p className="text-xs text-green-300/70 font-mono">
                  Set this video to play automatically in the live hero banner. Enter start and end times in seconds (e.g. 180 for 3 min).
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <input
                    type="number"
                    placeholder="Start Time (s)"
                    value={heroStart}
                    onChange={(e) => setHeroStart(e.target.value)}
                    className="bg-black/50 border border-green-500/30 rounded-lg px-3 py-1.5 text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-green-400 w-32 font-mono"
                  />
                  <span className="text-zinc-500 text-xs">to</span>
                  <input
                    type="number"
                    placeholder="End Time (s)"
                    value={heroEnd}
                    onChange={(e) => setHeroEnd(e.target.value)}
                    className="bg-black/50 border border-green-500/30 rounded-lg px-3 py-1.5 text-xs text-white placeholder:text-zinc-600 focus:outline-none focus:border-green-400 w-32 font-mono"
                  />
                  <button
                    onClick={async () => {
                      setIsSettingHero(true);
                      try {
                        const token = localStorage.getItem("vs_admin_token");
                        if (!token) {
                           alert("Admin session expired. Please log in again.");
                           return;
                        }
                        const res = await fetch("/api/admin/settings", {
                          method: "POST",
                          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                          body: JSON.stringify({
                            heroVideoId: video.id,
                            heroVideoStart: heroStart ? parseInt(heroStart) : 0,
                            heroVideoEnd: heroEnd ? parseInt(heroEnd) : 0
                          })
                        });
                        if (res.ok) alert("Cosmic Banner updated successfully! Reload the home page to see it.");
                        else alert("Failed to update banner.");
                      } catch (err) {
                        alert("Error updating banner.");
                      } finally {
                        setIsSettingHero(false);
                      }
                    }}
                    disabled={isSettingHero}
                    className={`px-4 py-1.5 rounded-lg text-xs font-bold font-mono tracking-wider transition-all border ${
                      isSettingHero
                        ? "bg-green-900 text-green-700 border-green-900 cursor-not-allowed"
                        : "bg-green-600 hover:bg-green-500 text-black border-green-400 shadow-[0_0_15px_rgba(34,197,94,0.3)] hover:scale-[1.02]"
                    }`}
                  >
                    {isSettingHero ? "SAVING..." : "SET AS BANNER CLIP"}
                  </button>
                </div>
              </div>
            )}

            {/* Description box */}
            <div className="p-4 sm:p-5 rounded-xl bg-zinc-950 border border-green-500/20 mb-8">
              <h3 className="text-green-400 font-bold text-sm mb-2 flex items-center gap-1.5">
                <Sparkles className="w-4 h-4" />
                <span>Details:</span>
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed whitespace-pre-wrap">
                {(video.description || "").replace(/<iframe[\s\S]*?<\/iframe>/gi, "").trim() || "Watch this video in full high definition smoothly without interruptions."}
              </p>
            </div>
            
            {/* Recommended Videos Section */}
            {recommendations.length > 0 ? (
              <>
                <h3 className="text-white font-bold text-base mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  <span>Recommended Videos</span>
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {recommendations.map((rec: any) => (
                    <div
                      id={`rec-video-card-${rec.uniqueId || rec.id}`}
                      key={rec.uniqueId || rec.id}
                      onClick={() => onVideoSelect(rec)}
                      className="group cursor-pointer rounded-xl bg-zinc-950 overflow-hidden border border-green-500/20 hover:border-green-400 hover:shadow-[0_0_15px_rgba(34,197,94,0.3)] transition-all duration-300 flex flex-col"
                    >
                      <div className="relative aspect-video bg-zinc-950 overflow-hidden flex-shrink-0 flex items-center justify-center">
                        <img
                          src={getProxiedThumbnailUrl(rec.thumbnailUrl, rec.title, rec.streamUrl || rec.videoUrl)}
                          alt={rec.title}
                          onError={(e) => {
                            const img = e.currentTarget;
                            const attempt = parseInt(img.getAttribute("data-attempt") || "0", 10);
                            if (attempt === 0) {
                              img.setAttribute("data-attempt", "1");
                              const pageUrl = rec.streamUrl || rec.videoUrl;
                              if (pageUrl) {
                                img.src = `/api/smart-thumb?pageUrl=${encodeURIComponent(pageUrl)}`;
                                return;
                              }
                            }
                            img.setAttribute("data-attempt", "2");
                            img.src = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360" fill="%230f172a"><defs><linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="%230f172a"/><stop offset="100%" stop-color="%23020617"/></linearGradient></defs><rect width="100%" height="100%" fill="url(%23g)"/><circle cx="320" cy="150" r="38" fill="%2322c55e" fill-opacity="0.25"/><polygon points="312,135 336,150 312,165" fill="%2322c55e"/><text x="50%" y="78%" fill="%23cbd5e1" font-family="sans-serif" font-weight="bold" font-size="15" text-anchor="middle">STREAM READY</text><text x="50%" y="88%" fill="%2322c55e" font-family="monospace" font-weight="bold" font-size="11" text-anchor="middle">⚡ ULTRA HD</text></svg>`;
                          }}
                          referrerPolicy="no-referrer"
                          style={{ willChange: 'transform', backfaceVisibility: 'hidden' }}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 relative z-10"
                        />
                        <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors z-20"></div>
                        <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/80 text-[10px] font-bold text-green-400 tracking-wider flex items-center gap-1 border border-green-500/30 z-30">
                          <Clock className="w-2.5 h-2.5" />
                          {rec.duration}
                        </div>
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-semibold text-zinc-200 line-clamp-2 group-hover:text-green-400 leading-snug">
                          {rec.title}
                        </h4>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-10 bg-zinc-950 rounded-xl border border-green-500/20">
                <p className="text-zinc-500 text-sm font-mono">No other videos found in this collection.</p>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
